/**
 * Draft Programme — Documents → Order of Events & Programme panel.
 *
 * Owner spec (Feb 2026): the Draft Programme is now generatable BEFORE
 * the Skating Order Draw has been completed. The draw card was moved
 * to ``CompetitionDrawsPanel.js`` — this panel only renders the
 * download + email tiles. The backend (``generate_draft_programme``)
 * falls back to entries sorted by club abbreviation → skater first
 * name when no draw exists, and stamps a red bordered warning on the
 * cover page (see ``utils/draft_programme_doc.py``).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Download, Mail } from 'lucide-react';
import { toast } from 'sonner';
import CoveringEmailDialog from './CoveringEmailDialog';
import LastSentLine from './LastSentLine';


export default function DraftProgrammePanel({ competitionsWithOOS = [], competitionId = '' }) {
  const draftProgComp = competitionId;
  const [downloadingDraftProg, setDownloadingDraftProg] = useState(false);
  const [downloadingDraftProgScreen, setDownloadingDraftProgScreen] = useState(false);

  // Email tile state — multi-attachment selection
  const [clubsWithEntries, setClubsWithEntries] = useState([]);
  const [selectedClub, setSelectedClub] = useState('all');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailVersions, setEmailVersions] = useState({ print: true, screen: true });

  const fetchClubsWithEntries = async (compId) => {
    if (!compId) { setClubsWithEntries([]); return; }
    try {
      const res = await axios.get(
        `${API}/draft-programme-email/${compId}/preflight`,
        { withCredentials: true }
      );
      setClubsWithEntries(res.data?.targets || []);
    } catch {
      setClubsWithEntries([]);
    }
  };

  useEffect(() => {
    setSelectedClub('all');
    fetchClubsWithEntries(draftProgComp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftProgComp]);

  const handleDownloadDraftProgramme = async () => {
    if (!draftProgComp) return;
    setDownloadingDraftProg(true);
    try {
      const response = await axios.get(
        `${API}/order-of-skate/${draftProgComp}/draft-programme`,
        { withCredentials: true, responseType: 'blob' }
      );
      const disposition = response.headers['content-disposition'] || '';
      let filename = 'Draft_Programme.docx';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Draft Programme (Print) downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate Draft Programme');
    } finally {
      setDownloadingDraftProg(false);
    }
  };

  const handleDownloadDraftProgrammeScreen = async () => {
    if (!draftProgComp) return;
    setDownloadingDraftProgScreen(true);
    try {
      const response = await axios.get(
        `${API}/order-of-skate/${draftProgComp}/draft-programme-screen`,
        { withCredentials: true, responseType: 'blob' }
      );
      const disposition = response.headers['content-disposition'] || '';
      let filename = 'Draft_Programme_Screen.docx';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Draft Programme (Screen) downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate Draft Programme (Screen)');
    } finally {
      setDownloadingDraftProgScreen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-stone-800 mb-2">Draft Programme</h3>
        <p className="text-sm text-muted-foreground">
          Generate the draft competition programme. Draws can be completed before
          or after distribution &mdash; the programme will mark missing draws on
          its cover page.
        </p>
      </div>

      {!draftProgComp && (
        <div className="rounded-lg border bg-stone-50 px-3 py-3 text-sm text-stone-600">
          Pick a competition above to continue.
          {competitionsWithOOS.length === 0 && (
            <span className="block text-xs text-amber-600 mt-1">
              No competitions with a generated Order of Skate found.
            </span>
          )}
        </div>
      )}

      {draftProgComp && !competitionsWithOOS.find(c => c.competition_id === draftProgComp) && (
        <div className="rounded-lg border bg-amber-50 px-3 py-3 text-sm text-amber-900">
          This competition doesn&apos;t have an Order of Skate yet. Generate one first.
        </div>
      )}

      {draftProgComp && competitionsWithOOS.find(c => c.competition_id === draftProgComp) && (
        <>
          {/* TILE 1: Create Draft Programme — download only */}
          <Card
            className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
            data-testid="programme-tile"
          >
            <div>
              <h4 className="text-base font-semibold text-stone-800">
                Create Draft Programme for Download
              </h4>
            </div>

            <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
              <Button
                onClick={handleDownloadDraftProgramme}
                disabled={downloadingDraftProg}
                className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
                data-testid="download-draft-programme-print-btn"
              >
                <Download className={`mr-2 h-4 w-4 ${downloadingDraftProg ? 'animate-pulse' : ''}`} />
                {downloadingDraftProg ? 'Generating...' : 'Draft Programme (Print)'}
              </Button>
              <div>
                <p className="text-sm font-medium text-stone-800">Generate draft for print and download</p>
                <p className="text-xs text-stone-600">Generates a draft programme file optimised for printing.</p>
              </div>
            </div>

            <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
              <Button
                onClick={handleDownloadDraftProgrammeScreen}
                disabled={downloadingDraftProgScreen}
                className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px]"
                data-testid="download-draft-programme-screen-btn"
              >
                <Download className={`mr-2 h-4 w-4 ${downloadingDraftProgScreen ? 'animate-pulse' : ''}`} />
                {downloadingDraftProgScreen ? 'Generating...' : 'Draft Programme (Screen)'}
              </Button>
              <div>
                <p className="text-sm font-medium text-stone-800">Generate draft for screen and download</p>
                <p className="text-xs text-stone-600">Generates a draft programme file optimised for screen use.</p>
              </div>
            </div>
          </Card>

          {/* TILE 2: Email Draft Programme to Clubs */}
          <Card
            className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
            data-testid="dp-email-tile"
          >
            <div>
              <h4 className="text-base font-semibold text-stone-800">Email Draft Programme to Clubs</h4>
              <p className="text-sm text-stone-600 mt-1">
                Generate draft programmes for clubs and send out by email.
              </p>
              <LastSentLine competitionId={draftProgComp} kind="draft-programme" />
            </div>

            <div>
              <Label htmlFor="dp-club-select" className="text-sm font-medium text-stone-700">
                Clubs (Select individual clubs or All Clubs Option)
              </Label>
              <select
                id="dp-club-select"
                className="w-full border rounded-lg px-3 py-2 mt-1 text-sm"
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                data-testid="dp-club-select"
              >
                <option value="all">{`All Clubs (${clubsWithEntries.length} clubs)`}</option>
                {clubsWithEntries.map((c) => (
                  <option key={c.club_id} value={c.club_id}>
                    {`${c.name}${c.missing_email ? ' (no email on file)' : ''}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <h5 className="text-sm font-semibold text-stone-800 mb-2">Attachments</h5>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-stone-800">
                  <input
                    type="checkbox"
                    checked={emailVersions.print}
                    onChange={(e) => setEmailVersions(v => ({ ...v, print: e.target.checked }))}
                    data-testid="dp-attach-print-checkbox"
                  />
                  Attach Draft Programme (Optimised for print)
                </label>
                <label className="flex items-center gap-2 text-sm text-stone-800">
                  <input
                    type="checkbox"
                    checked={emailVersions.screen}
                    onChange={(e) => setEmailVersions(v => ({ ...v, screen: e.target.checked }))}
                    data-testid="dp-attach-screen-checkbox"
                  />
                  Attach Draft Programme (Optimised for screen)
                </label>
              </div>
            </div>

            <Button
              onClick={() => setEmailDialogOpen(true)}
              disabled={!emailVersions.print && !emailVersions.screen}
              className="btn-primary"
              data-testid="email-draft-programmes-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              Email Draft Programmes
            </Button>
          </Card>
        </>
      )}

      <CoveringEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        competitionId={draftProgComp}
        clubId={selectedClub}
        endpointBase="draft-programme-email"
        title="Email Draft Programmes to Clubs"
        description="Compose the covering email. The selected Draft Programme .docx file(s) will be attached to every email."
        testIdPrefix="dp-email"
        extraSendBody={{
          versions: [
            ...(emailVersions.print ? ['print'] : []),
            ...(emailVersions.screen ? ['screen'] : []),
          ],
        }}
      />
    </div>
  );
}
